import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Truck, MapPin, Grid3X3, Receipt, Wallet, Plus, Pencil, AlertTriangle,
  Download, Search, RefreshCw, Filter, CircleDollarSign, IndianRupee,
  ClipboardCheck, ShoppingCart, User,
} from "lucide-react";
import { toast } from "sonner";
import {
  PendingLocationsTab,
  MarketReceivablesTab,
  TruckPrimaryDriverDialog,
} from "./LogisticsHubExtensions";

const fmt = (n) => "₹" + Math.round(Number(n || 0)).toLocaleString("en-IN");

function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function LogisticsHub() {
  const { user } = useAuth();
  // Phase 15.14 — Logistics Executive role: blocked from Imprest tabs + dashboard tiles
  const canSeeImprest = user?.role !== "logistics_executive";
  const [tab, setTab] = useState(canSeeImprest ? "dashboard" : "dashboard");
  // Bounce to a safe tab if the user lands on an imprest URL they can't access
  useEffect(() => {
    if (!canSeeImprest && (tab === "imprest" || tab === "lm-imprest")) {
      setTab("dashboard");
    }
  }, [canSeeImprest, tab]);
  const [dash, setDash] = useState({});
  useEffect(() => {
    api.get("/logistics/dashboard").then(r => setDash(r.data)).catch(() => {});
  }, [tab]);

  return (
    <div data-testid="logistics-page" className="space-y-5">
      <div className="flex items-start gap-3">
        <Truck className="h-7 w-7 text-red-600 mt-1 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 8 · Logistics</div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Logistics Hub
          </h1>
          <p className="text-xs text-slate-500 mt-0.5 hidden sm:block">
            Trucks, locations, route-rate grid, FASTag balances, trip ledger,
            and per-driver imprest with optional salary recovery.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <DashTile label="Low FASTag trucks" value={dash.low_fastag_trucks ?? 0}
                  note={`Threshold ${fmt(dash.low_fastag_threshold)}`} tone="amber" />
        <DashTile label="FASTag pending today" value={dash.fastag_pending_today ?? 0}
                  note={dash.fastag_non_working_day ? "Non-working day" : "Updates pending"} tone="amber" />
        <DashTile label="Open trips" value={dash.open_trips ?? 0} tone="blue" />
        {canSeeImprest && (
          <DashTile label="Driver-wise pending" value={fmt(dash.total_driver_pending)} tone="red" />
        )}
        {canSeeImprest && (
          <DashTile label="LM Imprest balance" value={fmt(dash.lm_imprest_balance)}
                    note={dash.lm_imprest_low ? `LOW (< ${fmt(dash.lm_imprest_threshold)})` : `Threshold ${fmt(dash.lm_imprest_threshold)}`}
                    tone={dash.lm_imprest_low ? "red" : "blue"} />
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        {/* Mobile: horizontally scrollable; sm+: wraps to multi-row */}
        <TabsList className="!h-auto flex flex-nowrap sm:flex-wrap overflow-x-auto justify-start sm:justify-center w-full no-scrollbar gap-1">
          <TabsTrigger value="dashboard" className="flex-shrink-0">Trips</TabsTrigger>
          <TabsTrigger value="fastag" className="flex-shrink-0">FASTag</TabsTrigger>
          <TabsTrigger value="fastag-ledger" data-testid="tab-fastag-ledger" className="flex-shrink-0">
            <Receipt className="h-3.5 w-3.5 mr-1" /> FASTag Ledger
          </TabsTrigger>
          <TabsTrigger value="trucks" className="flex-shrink-0">Trucks</TabsTrigger>
          <TabsTrigger value="locations" className="flex-shrink-0">Locations</TabsTrigger>
          <TabsTrigger value="rates" className="flex-shrink-0">Route Rates</TabsTrigger>
          {canSeeImprest && (
            <TabsTrigger value="imprest" data-testid="tab-imprest" className="flex-shrink-0">Driver Imprest</TabsTrigger>
          )}
          {canSeeImprest && (
            <TabsTrigger value="lm-imprest" data-testid="tab-lm-imprest" className="flex-shrink-0">LM Imprest</TabsTrigger>
          )}
          <TabsTrigger value="pending-locs" data-testid="tab-pending-locs" className="flex-shrink-0">
            <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Pending Locations
          </TabsTrigger>
          <TabsTrigger value="mkt-receivables" data-testid="tab-mkt-receivables" className="flex-shrink-0">
            <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Market Trip
          </TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-4"><TripsTab /></TabsContent>
        <TabsContent value="fastag" className="mt-4"><FastagTab /></TabsContent>
        <TabsContent value="fastag-ledger" className="mt-4"><FastagLedgerTab /></TabsContent>
        <TabsContent value="trucks" className="mt-4"><TrucksTab /></TabsContent>
        <TabsContent value="locations" className="mt-4"><LocationsTab /></TabsContent>
        <TabsContent value="rates" className="mt-4"><RouteRatesTab /></TabsContent>
        <TabsContent value="imprest" className="mt-4"><ImprestTab /></TabsContent>
        <TabsContent value="lm-imprest" className="mt-4"><LMImprestTab /></TabsContent>
        <TabsContent value="pending-locs" className="mt-4"><PendingLocationsTab /></TabsContent>
        <TabsContent value="mkt-receivables" className="mt-4"><MarketReceivablesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function DashTile({ label, value, note, tone }) {
  const cls = { amber: "text-amber-700", blue: "text-blue-700", red: "text-red-700" }[tone];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${cls}`}>{value}</div>
      {note && <div className="text-[10px] text-slate-400 mt-0.5">{note}</div>}
    </div>
  );
}

function Stat({ label, v, tone }) {
  const cls = {
    emerald: "text-emerald-700 bg-emerald-50 ring-emerald-200",
    blue: "text-blue-700 bg-blue-50 ring-blue-200",
    amber: "text-amber-700 bg-amber-50 ring-amber-200",
  }[tone] || "text-slate-700 bg-slate-50 ring-slate-200";
  return (
    <div className={`ring-1 rounded-md px-2 py-1.5 ${cls}`}>
      <div className="text-[9px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-bold leading-tight">{v}</div>
    </div>
  );
}

// ===========================================================================
// TRUCKS
// ===========================================================================
function TrucksTab() {
  const [rows, setRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    truck_no: "", model: "", truck_type: "", fastag_tag_id: "",
    min_fastag_balance: "",
  });
  const [warning, setWarning] = useState(null);
  const [primaryFor, setPrimaryFor] = useState(null);

  const refresh = () => {
    api.get("/logistics/trucks").then(r => setRows(r.data));
    api.get("/logistics/drivers").then(r => setDrivers(r.data || [])).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  const driverName = (id) =>
    (drivers.find((d) => d.id === id) || {}).name;

  const openAdd = () => {
    setEditId(null);
    setForm({ truck_no: "", model: "", truck_type: "", fastag_tag_id: "",
      min_fastag_balance: "" });
    setWarning(null);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      truck_no: t.truck_no, model: t.model || "", truck_type: t.truck_type || "",
      fastag_tag_id: t.fastag_tag_id || "",
      min_fastag_balance: t.min_fastag_balance ?? "",
    });
    setWarning(null);
    setOpen(true);
  };

  const onSave = async (allowWarning = false) => {
    try {
      const payload = { ...form };
      payload.min_fastag_balance = payload.min_fastag_balance === ""
        ? null : Number(payload.min_fastag_balance);
      if (editId) {
        // PATCH — never sends truck_no
        const { truck_no, ...patch } = payload;
        await api.patch(`/logistics/trucks/${editId}`, patch);
        toast.success("Truck updated");
      } else {
        await api.post(`/logistics/trucks${allowWarning ? "?allow_warning=true" : ""}`, payload);
        toast.success("Truck added");
      }
      setOpen(false);
      setForm({ truck_no: "", model: "", truck_type: "", fastag_tag_id: "",
        min_fastag_balance: "" });
      setWarning(null);
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.warning) {
        setWarning(detail);
      } else {
        toast.error(formatApiErrorDetail(detail) || "Save failed");
      }
    }
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Delete truck ${t.truck_no}? If it has trips it will be deactivated instead.`)) return;
    try {
      const r = await api.delete(`/logistics/trucks/${t.id}`);
      toast.success(r.data?.soft_deleted ? `Truck ${t.truck_no} deactivated (has trips)` : `Truck ${t.truck_no} removed`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-xs text-slate-500">{rows.length} trucks</div>
        <Button onClick={openAdd}
                className="brand-bg brand-bg-hover text-white" data-testid="add-truck-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Truck
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="text-left px-3 py-2">Truck No.</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Model</th>
              <th className="text-left px-3 py-2">FASTag Tag</th>
              <th className="text-right px-3 py-2">Min FASTag ₹</th>
              <th className="text-left px-3 py-2">Primary Driver</th>
              <th className="text-center px-3 py-2">Active</th>
              <th className="text-center px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(t => (
              <tr key={t.id} className="border-t border-slate-100"
                  data-testid={`truck-row-${t.truck_no}`}>
                <td className="px-3 py-2 font-mono text-sm">{t.truck_no}</td>
                <td className="px-3 py-2 text-xs">{t.truck_type || "—"}</td>
                <td className="px-3 py-2 text-xs">{t.model || "—"}</td>
                <td className="px-3 py-2 text-xs">{t.fastag_tag_id || "—"}</td>
                <td className="px-3 py-2 text-xs text-right font-mono">
                  {t.min_fastag_balance == null ? <span className="text-slate-400">global</span> : fmt(t.min_fastag_balance)}
                </td>
                <td className="px-3 py-2 text-xs">
                  <button
                    onClick={() => setPrimaryFor(t)}
                    className="inline-flex items-center gap-1 text-bravura-700 hover:underline"
                    data-testid={`truck-primary-${t.truck_no}`}>
                    <User className="h-3 w-3" />
                    {t.primary_driver_id ? (driverName(t.primary_driver_id) || "—") : "Assign"}
                  </button>
                </td>
                <td className="text-center">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                    t.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"
                  }`}>{t.active ? "Active" : "Inactive"}</span>
                </td>
                <td className="text-center whitespace-nowrap">
                  <Button size="sm" variant="ghost"
                          data-testid={`truck-edit-${t.truck_no}`}
                          onClick={() => openEdit(t)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          data-testid={`truck-delete-${t.truck_no}`}
                          onClick={() => onDelete(t)}>
                    ×
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-xs text-slate-400">
                No trucks yet — click "Add Truck"
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <TruckPrimaryDriverDialog
        truck={primaryFor}
        open={!!primaryFor}
        onClose={() => setPrimaryFor(null)}
        onSaved={refresh}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Truck" : "Add Truck"}</DialogTitle>
            <DialogDescription>
              Indian plate format e.g. RJ14GU0449. Set per-truck FASTag alert threshold below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Truck Number *</Label>
            <Input value={form.truck_no}
                   onChange={(e) => { setForm({ ...form, truck_no: e.target.value.toUpperCase() }); setWarning(null); }}
                   data-testid="add-truck-no-input"
                   disabled={!!editId}
                   placeholder="RJ14GU0449" className="font-mono" />
            <Label className="text-xs">Type</Label>
            <Input value={form.truck_type} onChange={(e) => setForm({ ...form, truck_type: e.target.value })}
                   placeholder="14ft / 17ft / Container" />
            <Label className="text-xs">Model</Label>
            <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            <Label className="text-xs">FASTag Tag ID</Label>
            <Input value={form.fastag_tag_id}
                   onChange={(e) => setForm({ ...form, fastag_tag_id: e.target.value })} />
            <Label className="text-xs">Min FASTag balance (₹)</Label>
            <Input type="number" min="0"
                   value={form.min_fastag_balance}
                   onChange={(e) => setForm({ ...form, min_fastag_balance: e.target.value })}
                   placeholder="Leave blank → use global (₹2000)"
                   data-testid="truck-min-fastag-input" />
            <div className="text-[10px] text-slate-500">
              Email Accounts when balance falls below this for this truck.
            </div>
            {warning && (
              <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-900 p-2 rounded mt-2">
                ⚠ {warning.warning}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            {warning ? (
              <Button variant="outline" onClick={() => onSave(true)}
                      data-testid="add-truck-confirm-warning">Confirm anyway</Button>
            ) : (
              <Button onClick={() => onSave(false)}
                      data-testid="truck-save-btn"
                      className="brand-bg brand-bg-hover text-white">Save</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===========================================================================
// LOCATIONS
// ===========================================================================
function LocationsTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const refresh = () => api.get("/logistics/locations").then(r => setRows(r.data));
  useEffect(() => { refresh(); }, []);

  const onDelete = async (l) => {
    if (!window.confirm(`Delete location "${l.name}"? If used in trips it will be deactivated instead.`)) return;
    try {
      const r = await api.delete(`/logistics/locations/${l.id}`);
      toast.success(r.data?.soft_deleted ? `${l.name} deactivated (has trips)` : `${l.name} removed`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const downloadSample = async () => {
    try {
      const res = await api.get("/logistics/locations/sample-template",
                                  { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "locations_sample.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Sample downloaded ✓");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
    }
  };

  const onUpload = async (e, dryRun = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `/logistics/locations/bulk-upload${dryRun ? "?dry_run=true" : ""}`;
      const { data } = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(data);
      if (!dryRun) {
        toast.success(
          `Done — ${data.created} created, ${data.updated} updated, ${data.skipped} skipped`,
        );
        refresh();
      } else {
        toast.message(
          `Preview — would create ${data.created}, update ${data.updated}, skip ${data.skipped}`,
        );
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";  // reset so same file can be re-picked
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 justify-between items-center">
        <h3 className="text-sm font-bold">Client Master ({rows.length})</h3>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm"
                  onClick={downloadSample}
                  data-testid="loc-sample-download">
            <Download className="h-3.5 w-3.5 mr-1" /> Sample XLSX
          </Button>
          <label className="inline-flex">
            <Button variant="outline" size="sm" asChild>
              <span data-testid="loc-bulk-upload">
                <Plus className="h-3.5 w-3.5 mr-1" />
                {uploading ? "Uploading…" : "Bulk Upload"}
              </span>
            </Button>
            <input type="file" accept=".xlsx,.xlsm" className="hidden"
                   disabled={uploading}
                   onChange={(e) => onUpload(e, false)}
                   data-testid="loc-bulk-upload-input" />
          </label>
          <Button onClick={() => setEditing({})} className="brand-bg brand-bg-hover text-white" data-testid="add-location-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Client
          </Button>
        </div>
      </div>

      {uploadResult && (
        <div className="bg-white ring-1 ring-slate-200 rounded-lg p-3 text-xs space-y-2"
             data-testid="loc-upload-result">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-700">
              Upload result {uploadResult.dry_run ? "(preview)" : ""}
            </div>
            <Button variant="ghost" size="sm"
                    onClick={() => setUploadResult(null)}>×</Button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Created" v={uploadResult.created} tone="emerald" />
            <Stat label="Updated" v={uploadResult.updated} tone="blue" />
            <Stat label="Skipped" v={uploadResult.skipped} tone="amber" />
          </div>
          {uploadResult.errors?.length > 0 && (
            <details>
              <summary className="cursor-pointer text-red-700">
                {uploadResult.errors.length} errors
              </summary>
              <ul className="ml-4 list-disc text-red-700">
                {uploadResult.errors.map((er, i) => (
                  <li key={i}>Row {er.row}: <b>{er.name}</b> — {er.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="text-left px-3 py-2">Client</th>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-left px-3 py-2">Contact</th>
              <th className="text-left px-3 py-2">Location</th>
              <th className="text-center">Status</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(l => (
              <tr key={l.id} className="border-t border-slate-100" data-testid={`loc-row-${l.name}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{l.name}</div>
                  {l.name_hindi && <div className="text-[10px] text-slate-500">{l.name_hindi}</div>}
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{l.client_code || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-700">
                  {l.contact_name && <div>{l.contact_name}</div>}
                  {l.contact_phone && <div className="text-slate-500">{l.contact_phone}</div>}
                </td>
                <td className="px-3 py-2 text-xs">
                  {l.address && (
                    <div className="text-slate-700 max-w-[320px] truncate"
                         title={l.address}>{l.address}</div>
                  )}
                  {l.latitude && l.longitude ? (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${l.latitude},${l.longitude}`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`navigate-${l.id}`}
                      className="text-bravura hover:underline inline-flex items-center gap-1 text-[10px]"
                    >
                      📍 Navigate
                    </a>
                  ) : l.address ? (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(l.address)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-slate-500 hover:underline text-[10px]"
                    >
                      Open in Maps
                    </a>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="text-center text-xs">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    l.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"
                  }`}>{l.active ? "Active" : "Inactive"}</span>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(l)} data-testid={`loc-edit-${l.name}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700"
                          data-testid={`loc-delete-${l.name}`} onClick={() => onDelete(l)}>
                    ×
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-xs text-slate-400">No clients yet. Click "Add Client" to start.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editing !== null && (
        <ClientMasterDialog
          client={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function ClientMasterDialog({ client, onCancel, onSaved }) {
  const isEdit = !!client.id;
  const [form, setForm] = useState({
    name: client.name || "",
    client_code: client.client_code || "",
    address: client.address || "",
    latitude: client.latitude ?? "",
    longitude: client.longitude ?? "",
    contact_name: client.contact_name || "",
    contact_phone: client.contact_phone || "",
    notes: client.notes || "",
    active: client.active !== false,
  });
  const [busy, setBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);

  const captureGPS = () => {
    if (!navigator.geolocation) return toast.error("GPS not available");
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => ({ ...f,
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6) }));
        setGeoBusy(false);
        toast.success("GPS captured");
      },
      (err) => { setGeoBusy(false); toast.error(err.message || "GPS denied"); },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        active: form.active,
        client_code: form.client_code || null,
        address: form.address || null,
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
        contact_name: form.contact_name || null,
        contact_phone: form.contact_phone || null,
        notes: form.notes || null,
      };
      if (isEdit) {
        await api.patch(`/logistics/locations/${client.id}`, payload);
      } else {
        await api.post("/logistics/locations", payload);
      }
      toast.success(isEdit ? "Client updated" : "Client added");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 flex items-center justify-center p-4" data-testid="client-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b">
          <h4 className="text-base font-bold">{isEdit ? "Edit Client" : "Add Client"}</h4>
        </div>
        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Client Name *</label>
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              data-testid="client-name"
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Client Code</label>
            <input value={form.client_code} onChange={e => setForm({...form, client_code: e.target.value})}
              data-testid="client-code"
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm" />
          </div>
          <div className="flex items-end gap-2">
            <div className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} />
              Active
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Address</label>
            <textarea rows={2} value={form.address} onChange={e => setForm({...form, address: e.target.value})}
              data-testid="client-address"
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Latitude</label>
            <input value={form.latitude} onChange={e => setForm({...form, latitude: e.target.value})}
              placeholder="e.g. 26.4499"
              data-testid="client-lat"
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Longitude</label>
            <input value={form.longitude} onChange={e => setForm({...form, longitude: e.target.value})}
              placeholder="e.g. 74.6399"
              data-testid="client-lng"
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm" />
          </div>
          <div className="col-span-2 -mt-2 flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={captureGPS}
              disabled={geoBusy} data-testid="client-gps">
              {geoBusy ? "Capturing…" : "📍 Use my current GPS"}
            </Button>
            {form.latitude && form.longitude && (
              <a target="_blank" rel="noopener noreferrer"
                href={`https://www.google.com/maps/search/?api=1&query=${form.latitude},${form.longitude}`}
                className="text-xs text-bravura hover:underline self-center">Preview pin →</a>
            )}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Contact Name</label>
            <input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})}
              data-testid="client-contact-name"
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Contact Phone</label>
            <input value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})}
              data-testid="client-contact-phone"
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Notes (gate timings, instructions…)</label>
            <textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
              data-testid="client-notes"
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm" />
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="client-save" className="brand-bg text-white">
            {busy ? "Saving…" : "Save Client"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// ROUTE RATES (matrix)
// ===========================================================================
function RouteRatesTab() {
  const [data, setData] = useState({ locations: [], trucks: [], grid: {} });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editKey, setEditKey] = useState(null); // "loc|truck" if editing single
  const [form, setForm] = useState({
    location_id: "",
    truck_nos: [],
    rto: 0, cash: 0, tolls: 0, dala: 0, unloading: 0, other: 0,
    amount_to_pay: "",
  });
  const [search, setSearch] = useState("");

  const refresh = async () => {
    setLoading(true);
    const r = await api.get("/logistics/route-rates");
    setData(r.data);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const expectedTotal = useMemo(() => (
    Number(form.rto || 0) + Number(form.cash || 0) + Number(form.tolls || 0)
    + Number(form.dala || 0) + Number(form.unloading || 0)
    + Number(form.other || 0)
  ), [form]);

  const openAdd = () => {
    setEditKey(null);
    setForm({
      location_id: "", truck_nos: [],
      rto: 0, cash: 0, tolls: 0, dala: 0, unloading: 0, other: 0,
      amount_to_pay: "",
    });
    setOpen(true);
  };

  const openEdit = (locId, truckNo) => {
    const cell = data.grid[`${locId}|${truckNo}`] || {};
    setEditKey(`${locId}|${truckNo}`);
    setForm({
      location_id: locId, truck_nos: [truckNo],
      rto: cell.rto || 0, cash: cell.cash || 0, tolls: cell.tolls || 0,
      dala: cell.dala || 0, unloading: cell.unloading || 0, other: cell.other || 0,
      amount_to_pay: cell.amount_to_pay ?? "",
    });
    setOpen(true);
  };

  const onSave = async () => {
    if (!form.location_id) return toast.error("Location is required");
    if (!form.truck_nos.length) return toast.error("Select at least one truck");
    if (expectedTotal <= 0) return toast.error("Add at least one expense component");
    const rows = form.truck_nos.map((tn) => ({
      location_id: form.location_id,
      truck_no: tn,
      rto: Number(form.rto || 0),
      cash: Number(form.cash || 0),
      tolls: Number(form.tolls || 0),
      dala: Number(form.dala || 0),
      unloading: Number(form.unloading || 0),
      other: Number(form.other || 0),
      amount_to_pay: form.amount_to_pay === "" ? null : Number(form.amount_to_pay),
    }));
    try {
      await api.post("/logistics/route-rates/bulk", { rows });
      toast.success(`Saved rate for ${rows.length} truck(s)`);
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const onDelete = async (locId, truckNo) => {
    if (!window.confirm(`Remove rate for ${truckNo} on this route?`)) return;
    await api.post("/logistics/route-rates/bulk", {
      rows: [{
        location_id: locId, truck_no: truckNo,
        rto: 0, cash: 0, tolls: 0, dala: 0, unloading: 0, other: 0,
      }],
    });
    refresh();
  };

  const toggleTruck = (tn) => {
    setForm((f) => ({
      ...f,
      truck_nos: f.truck_nos.includes(tn)
        ? f.truck_nos.filter((x) => x !== tn)
        : [...f.truck_nos, tn],
    }));
  };

  // Flatten grid → list view of existing rates, grouped by location
  const rows = useMemo(() => {
    const list = [];
    Object.entries(data.grid || {}).forEach(([k, cell]) => {
      const [locId, truckNo] = k.split("|");
      const loc = data.locations.find((l) => l.id === locId);
      list.push({ key: k, locId, truckNo, locName: loc?.name || "(unknown)", ...cell });
    });
    list.sort((a, b) => a.locName.localeCompare(b.locName)
        || a.truckNo.localeCompare(b.truckNo));
    if (search) {
      const q = search.toLowerCase();
      return list.filter((r) => r.locName.toLowerCase().includes(q)
          || r.truckNo.toLowerCase().includes(q));
    }
    return list;
  }, [data, search]);

  if (loading) return <div className="text-xs text-slate-400">Loading…</div>;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by location or truck"
            className="h-8 w-64 text-xs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="rate-search"
          />
          <span className="text-xs text-slate-500">
            {rows.length} rate{rows.length === 1 ? "" : "s"}
          </span>
        </div>
        <Button onClick={openAdd} className="brand-bg brand-bg-hover text-white"
                data-testid="rate-add-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add / Update Rate
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="text-left px-3 py-2">Location</th>
              <th className="text-left px-3 py-2">Truck</th>
              <th className="text-right px-2 py-2">RTO</th>
              <th className="text-right px-2 py-2">Cash</th>
              <th className="text-right px-2 py-2">Tolls</th>
              <th className="text-right px-2 py-2">Dala</th>
              <th className="text-right px-2 py-2">Unloading</th>
              <th className="text-right px-2 py-2">Other</th>
              <th className="text-right px-2 py-2 bg-red-50">Total (Actual)</th>
              <th className="text-right px-2 py-2 bg-emerald-50">Pay</th>
              <th className="text-center px-2 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`rate-row-${r.locName}-${r.truckNo}`}>
                <td className="px-3 py-2 font-medium">{r.locName}</td>
                <td className="px-3 py-2 font-mono">{r.truckNo}</td>
                <td className="px-2 py-2 text-right font-mono">{r.rto || "—"}</td>
                <td className="px-2 py-2 text-right font-mono">{r.cash || "—"}</td>
                <td className="px-2 py-2 text-right font-mono">{r.tolls || "—"}</td>
                <td className="px-2 py-2 text-right font-mono">{r.dala || "—"}</td>
                <td className="px-2 py-2 text-right font-mono">{r.unloading || "—"}</td>
                <td className="px-2 py-2 text-right font-mono">{r.other || "—"}</td>
                <td className="px-2 py-2 text-right font-mono font-semibold bg-red-50">
                  {fmt(r.expected_amount)}
                </td>
                <td className="px-2 py-2 text-right font-mono font-semibold bg-emerald-50">
                  {r.amount_to_pay == null ? "—" : fmt(r.amount_to_pay)}
                </td>
                <td className="px-2 py-2 text-center whitespace-nowrap">
                  <Button size="sm" variant="ghost"
                          data-testid={`rate-edit-${r.locName}-${r.truckNo}`}
                          onClick={() => openEdit(r.locId, r.truckNo)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          data-testid={`rate-delete-${r.locName}-${r.truckNo}`}
                          onClick={() => onDelete(r.locId, r.truckNo)}>
                    ×
                  </Button>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={11} className="text-center py-6 text-xs text-slate-400">
                {data.locations.length && data.trucks.length
                  ? "No rates configured. Click Add / Update Rate to start."
                  : "Add locations & trucks first."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editKey ? "Update Route Rate" : "Add Route Rate"}</DialogTitle>
            <DialogDescription className="text-xs">
              Location ke saath multiple trucks select karke ek hi rate apply karein.
              Components ka sum = expected actual. Amount-to-pay alag se enter karein.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Location *</Label>
              <Select value={form.location_id}
                      onValueChange={(v) => setForm({ ...form, location_id: v })}
                      disabled={!!editKey}>
                <SelectTrigger data-testid="rate-loc-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {data.locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Trucks * {editKey ? "(locked while editing)" : "(select multiple)"}</Label>
              <div className="mt-1 max-h-48 overflow-y-auto border border-slate-200 rounded p-2 grid grid-cols-2 gap-1.5">
                {data.trucks.map((t) => (
                  <label key={t.truck_no}
                         className={`flex items-center gap-2 text-xs px-2 py-1 rounded cursor-pointer
                                       ${form.truck_nos.includes(t.truck_no)
                                          ? "bg-red-50 ring-1 ring-red-200" : "hover:bg-slate-50"}
                                       ${editKey ? "opacity-60 cursor-not-allowed" : ""}`}>
                    <Checkbox
                      checked={form.truck_nos.includes(t.truck_no)}
                      onCheckedChange={() => !editKey && toggleTruck(t.truck_no)}
                      disabled={!!editKey}
                      data-testid={`rate-truck-cb-${t.truck_no}`}
                    />
                    <span className="font-mono">{t.truck_no}</span>
                    {t.truck_type && <span className="text-[10px] text-slate-400">({t.truck_type})</span>}
                  </label>
                ))}
              </div>
              {!editKey && form.truck_nos.length > 0 && (
                <div className="text-[10px] text-slate-500 mt-1">
                  Selected {form.truck_nos.length} truck(s) — same rate will apply to all.
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs mb-2 block">Expense Components (₹)</Label>
              <div className="grid grid-cols-3 gap-2">
                {["rto","cash","tolls","dala","unloading","other"].map((k) => (
                  <div key={k}>
                    <Label className="text-[10px] capitalize text-slate-500">
                      {k === "unloading" ? "Unloading (driver)" : k}
                    </Label>
                    <Input type="number" min="0" value={form[k]}
                           onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                           className="h-8 font-mono text-xs"
                           data-testid={`rate-comp-${k}`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 bg-slate-50 border rounded p-3">
              <div>
                <Label className="text-[10px] uppercase text-slate-500">
                  Expected Actual (sum)
                </Label>
                <div className="text-lg font-semibold font-mono text-red-700"
                     data-testid="rate-expected-sum">
                  {fmt(expectedTotal)}
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-slate-500">
                  Amount To Pay (driver) ₹
                </Label>
                <Input type="number" min="0"
                       value={form.amount_to_pay}
                       placeholder={`Default: ${fmt(expectedTotal)}`}
                       onChange={(e) => setForm({ ...form, amount_to_pay: e.target.value })}
                       className="h-8 font-mono text-emerald-700 font-semibold"
                       data-testid="rate-amount-to-pay" />
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Blank → equals expected actual
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onSave} className="brand-bg brand-bg-hover text-white"
                    data-testid="rate-save-btn">
              Save {editKey ? "Update" : `for ${form.truck_nos.length || 0} truck(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===========================================================================
// FASTAG
// ===========================================================================
function FastagTab() {
  const [data, setData] = useState({ rows: [], threshold: 2000, today: "", non_working_day: false });
  const [open, setOpen] = useState(null);
  const [bal, setBal] = useState("");
  const [balDate, setBalDate] = useState(todayIso());
  const [note, setNote] = useState("");

  const refresh = () => api.get("/logistics/fastag/latest").then(r => setData(r.data));
  useEffect(() => { refresh(); }, []);

  const onSave = async () => {
    try {
      const res = await api.post("/logistics/fastag/balance", {
        truck_no: open.truck_no,
        balance: Number(bal),
        date: balDate,
        note: note || null,
      });
      if (res.data?.low_alert_sent) {
        toast.success("Updated · Low-balance email sent to Accounts");
      } else {
        toast.success("Updated");
      }
      setOpen(null); setBal(""); setNote(""); setBalDate(todayIso());
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const pendingCount = data.rows.filter(r => r.pending_today).length;

  return (
    <div className="space-y-3">
      <SurepassTollsPanel />
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="text-slate-500">
          Global threshold: <b>{fmt(data.threshold)}</b>
        </span>
        {data.non_working_day ? (
          <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-[10px]">
            Today is non-working (Sunday/Holiday) — pending check paused
          </span>
        ) : pendingCount > 0 ? (
          <span data-testid="fastag-pending-banner"
                className="bg-amber-100 text-amber-900 px-2 py-0.5 rounded text-[10px] inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> {pendingCount} truck(s) pending today's update
          </span>
        ) : data.rows.length > 0 ? (
          <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-[10px]">
            All trucks up-to-date today ✓
          </span>
        ) : null}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="text-left px-3 py-2">Truck</th>
              <th className="text-left px-3 py-2">Last Updated</th>
              <th className="text-right px-3 py-2">Latest Balance</th>
              <th className="text-right px-3 py-2">Threshold</th>
              <th className="text-center px-3 py-2">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(t => (
              <tr key={t.id} className="border-t border-slate-100"
                  data-testid={`fastag-row-${t.truck_no}`}>
                <td className="px-3 py-2 font-mono">{t.truck_no}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{t.latest_date || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {t.latest_balance == null ? "—" : fmt(t.latest_balance)}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {fmt(t.threshold)}
                </td>
                <td className="text-center">
                  <div className="flex gap-1 justify-center items-center">
                    {t.is_low && (
                      <span data-testid={`fastag-low-flag-${t.truck_no}`}
                            className="text-[10px] bg-red-100 text-red-800 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Low
                      </span>
                    )}
                    {t.pending_today && (
                      <span data-testid={`fastag-pending-${t.truck_no}`}
                            className="text-[10px] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">
                        Pending Today
                      </span>
                    )}
                    {!t.is_low && !t.pending_today && t.latest_balance && (
                      <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">OK</span>
                    )}
                    {!t.latest_balance && !t.pending_today && (
                      <span className="text-[10px] text-slate-400">no data</span>
                    )}
                  </div>
                </td>
                <td className="text-right pr-3">
                  <Button size="sm" variant="outline"
                          data-testid={`fastag-update-${t.truck_no}`}
                          onClick={() => { setOpen(t); setBal(""); setBalDate(todayIso()); setNote(""); }}>
                    Update
                  </Button>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-xs text-slate-400">No active trucks.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>FASTag balance — {open?.truck_no}</DialogTitle>
            <DialogDescription className="text-xs">
              Threshold for this truck: <b>{fmt(open?.threshold)}</b>. Email goes to
              Accounts automatically when balance falls below threshold.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={balDate}
                       max={todayIso()}
                       onChange={(e) => setBalDate(e.target.value)}
                       data-testid="fastag-date-input" />
              </div>
              <div>
                <Label className="text-xs">Current balance (₹)</Label>
                <Input type="number" value={bal} onChange={(e) => setBal(e.target.value)}
                       data-testid="fastag-balance-input" />
              </div>
            </div>
            <Label className="text-xs">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder="e.g. Recharged via Paytm ref XYZ"
                   data-testid="fastag-note-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(null)}>Cancel</Button>
            <Button onClick={onSave} className="brand-bg brand-bg-hover text-white"
                    data-testid="fastag-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===========================================================================
// TRIPS
// ===========================================================================
function TripsTab() {
  const [trips, setTrips] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [locations, setLocations] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filters, setFilters] = useState({
    date_from: "", date_to: "", truck_no: "", driver_id: "",
    status: "", variance_only: false,
  });
  const [open, setOpen] = useState(false);
  const [settleFor, setSettleFor] = useState(null);
  const [settleAmt, setSettleAmt] = useState("");
  const [form, setForm] = useState({
    date: todayIso(), truck_no: "", location_id: "", driver_id: "",
    expense: { rto: 0, cash: 0, tolls: 0, dala: 0, unloading: 0, other: 0, other_remark: "" },
    expected_amount_override: null, expected_override_reason: "",
    amount_to_pay: null, amount_to_pay_reason: "",
  });
  const [expectedRoute, setExpectedRoute] = useState(null);
  const [routeRate, setRouteRate] = useState(null); // full breakdown

  const refresh = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const [t, tr, lo, dr] = await Promise.all([
      api.get("/logistics/trips", { params }),
      api.get("/logistics/trucks"),
      api.get("/logistics/locations"),
      api.get("/driver-attendance/roster", { params: { date: todayIso() } }),
    ]);
    setTrips(t.data);
    setTrucks(tr.data);
    setLocations(lo.data);
    setDrivers((dr.data?.rows || []));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [JSON.stringify(filters)]);

  // Auto-resolve route rate when truck+location chosen — auto-fills expense + amount_to_pay
  useEffect(() => {
    if (form.truck_no && form.location_id) {
      api.get("/logistics/route-rates").then(r => {
        const k = `${form.location_id}|${form.truck_no}`;
        const cell = r.data.grid[k];
        if (cell && typeof cell === "object") {
          setExpectedRoute(cell.expected_amount ?? null);
          setRouteRate(cell);
          // Auto-fill only when current expense is all-zero (don't clobber edits)
          setForm((prev) => {
            const e = prev.expense;
            const allZero = ["rto","cash","tolls","dala","unloading","other"]
              .every((k) => Number(e[k] || 0) === 0);
            if (!allZero) return prev;
            return {
              ...prev,
              expense: {
                ...prev.expense,
                rto: cell.rto || 0, cash: cell.cash || 0, tolls: cell.tolls || 0,
                dala: cell.dala || 0, unloading: cell.unloading || 0, other: cell.other || 0,
              },
              amount_to_pay: prev.amount_to_pay == null || prev.amount_to_pay === ""
                ? (cell.amount_to_pay ?? "") : prev.amount_to_pay,
            };
          });
        } else if (typeof cell === "number") {
          // Defensive back-compat: server returns plain number
          setExpectedRoute(cell);
          setRouteRate(null);
        } else {
          setExpectedRoute(null);
          setRouteRate(null);
        }
      });
    } else {
      setExpectedRoute(null);
      setRouteRate(null);
    }
  }, [form.truck_no, form.location_id]);

  const actualTotal = useMemo(() => {
    const e = form.expense;
    return Number(e.rto || 0) + Number(e.cash || 0) + Number(e.tolls || 0)
         + Number(e.dala || 0) + Number(e.unloading || 0) + Number(e.other || 0);
  }, [form.expense]);

  const variance = expectedRoute != null ? actualTotal - expectedRoute : null;

  const onCreate = async () => {
    if (!form.truck_no || !form.location_id || !form.driver_id) {
      return toast.error("Truck, location & driver required");
    }
    try {
      await api.post("/logistics/trips", {
        ...form,
        amount_to_pay: form.amount_to_pay === "" ? null : form.amount_to_pay,
        expected_amount_override: form.expected_amount_override === "" ? null
                                  : form.expected_amount_override,
      });
      toast.success("Trip logged");
      setOpen(false);
      setForm({
        date: todayIso(), truck_no: "", location_id: "", driver_id: "",
        expense: { rto: 0, cash: 0, tolls: 0, dala: 0, unloading: 0, other: 0, other_remark: "" },
        expected_amount_override: null, expected_override_reason: "",
        amount_to_pay: null, amount_to_pay_reason: "",
      });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const onSettle = async () => {
    try {
      await api.post(`/logistics/trips/${settleFor.id}/settle`, {
        amount_paid: Number(settleAmt), settled: true,
      });
      toast.success("Settled");
      setSettleFor(null);
      setSettleAmt("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Settle failed");
    }
  };

  const onExport = async () => {
    const params = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    const r = await api.get("/logistics/export/trips", { params, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a"); a.href = url;
    a.download = `trips_${todayIso()}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="space-y-3">
      <div className="bg-white border rounded-lg p-3 flex flex-wrap gap-2 items-end">
        <div><Label className="text-[10px]">From</Label>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-8 w-36" />
        </div>
        <div><Label className="text-[10px]">To</Label>
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-8 w-36" />
        </div>
        <FilterSel label="Truck" value={filters.truck_no} options={[["","All"], ...trucks.map(t => [t.truck_no, t.truck_no])]} onChange={(v) => setFilters({...filters, truck_no: v})} />
        <FilterSel label="Status" value={filters.status} options={[["","All"], ["open","Open"], ["settled","Settled"]]} onChange={(v) => setFilters({...filters, status: v})} />
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Switch checked={filters.variance_only} onCheckedChange={(v) => setFilters({...filters, variance_only: v})} />
          Variance only
        </label>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={onExport} data-testid="trip-export-btn">
            <Download className="h-3.5 w-3.5 mr-1" /> Excel
          </Button>
          <Button onClick={() => setOpen(true)} className="brand-bg brand-bg-hover text-white"
                  data-testid="trip-add-btn">
            <Plus className="h-4 w-4 mr-1" /> Log Trip
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="text-left px-2 py-2">Date</th>
              <th className="text-left px-2 py-2">Truck</th>
              <th className="text-center">#</th>
              <th className="text-left px-2 py-2">Location</th>
              <th className="text-left px-2 py-2">Driver</th>
              <th className="text-right px-2 py-2">Actual</th>
              <th className="text-right px-2 py-2">Expected</th>
              <th className="text-right px-2 py-2">Variance</th>
              <th className="text-right px-2 py-2">To Pay</th>
              <th className="text-right px-2 py-2">Pending</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {trips.map(t => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`trip-row-${t.id}`}>
                <td className="px-2 py-2 text-xs">{t.date}</td>
                <td className="px-2 py-2 font-mono text-xs">{t.truck_no}</td>
                <td className="text-center text-xs">{t.trip_seq}</td>
                <td className="px-2 py-2 text-xs">{t.location_name}</td>
                <td className="px-2 py-2 text-xs">{t.driver_name}</td>
                <td className="px-2 py-2 text-right font-mono text-xs">{fmt(t.actual_total)}</td>
                <td className="px-2 py-2 text-right font-mono text-xs">{t.expected_amount == null ? "—" : fmt(t.expected_amount)}</td>
                <td className="px-2 py-2 text-right font-mono text-xs">
                  {t.variance == null ? "—" : (
                    <span data-testid={`trip-variance-${t.id}`}
                          className={t.variance_flagged ? "text-red-700 font-bold" : "text-slate-500"}>
                      {t.variance > 0 ? "+" : ""}{fmt(t.variance)}
                    </span>
                  )}
                </td>
                <td className="px-2 py-2 text-right font-mono text-xs">{fmt(t.amount_to_pay)}</td>
                <td className="px-2 py-2 text-right font-mono text-xs">{fmt(t.pending)}</td>
                <td className="px-2 py-2 text-right">
                  {t.status === "open" ? (
                    <Button size="sm" variant="outline"
                            data-testid={`trip-settle-${t.id}`}
                            onClick={() => { setSettleFor(t); setSettleAmt(String(t.pending)); }}>Settle</Button>
                  ) : <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">Settled</span>}
                </td>
              </tr>
            ))}
            {trips.length === 0 && (
              <tr><td colSpan={11} className="text-center py-6 text-xs text-slate-400">No trips match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw]">
          <DialogHeader><DialogTitle>Log Trip</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} max={todayIso()} className="w-full" />
            </div>
            <div><Label className="text-xs">Truck *</Label>
              <Select value={form.truck_no} onValueChange={(v) => setForm({...form, truck_no: v})}>
                <SelectTrigger data-testid="trip-truck-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{trucks.map(t => <SelectItem key={t.truck_no} value={t.truck_no}>{t.truck_no}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Location *</Label>
              <Select value={form.location_id} onValueChange={(v) => setForm({...form, location_id: v})}>
                <SelectTrigger data-testid="trip-loc-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs">Driver *</Label>
            <Select value={form.driver_id} onValueChange={(v) => setForm({...form, driver_id: v})}>
              <SelectTrigger data-testid="trip-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
              <SelectContent>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name} ({d.employee_code})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {["rto","cash","tolls","dala","unloading","other"].map(k => (
              <div key={k}>
                <Label className="text-[10px] capitalize">{k} (₹)</Label>
                <Input type="number" value={form.expense[k]}
                       onChange={(e) => setForm({...form, expense: {...form.expense, [k]: Number(e.target.value)}})}
                       className="h-8 font-mono text-xs"
                       data-testid={`trip-expense-${k}`} />
              </div>
            ))}
          </div>
          <div className="bg-slate-50 border rounded p-2 text-xs space-y-0.5">
            <div className="flex justify-between"><span>Actual total</span><b>{fmt(actualTotal)}</b></div>
            <div className="flex justify-between text-slate-600">
              <span>Expected (route rate)</span>
              <span>{expectedRoute == null ? "— (no rate set)" : fmt(expectedRoute)}</span>
            </div>
            {variance != null && (
              <div className={`flex justify-between font-semibold ${Math.abs(variance) > 50 ? "text-red-700" : "text-slate-700"}`}>
                <span>Variance</span>
                <span>{variance > 0 ? "+" : ""}{fmt(variance)}</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs flex items-center gap-1">
                Amount to pay (₹)
                {routeRate?.amount_to_pay != null && (
                  <span className="text-[9px] bg-emerald-100 text-emerald-800 px-1 py-0.5 rounded">
                    Auto from route rate
                  </span>
                )}
              </Label>
              <Input type="number"
                     value={form.amount_to_pay ?? ""}
                     placeholder={routeRate?.amount_to_pay != null
                                   ? String(routeRate.amount_to_pay)
                                   : String(actualTotal)}
                     onChange={(e) => setForm({ ...form, amount_to_pay: e.target.value === "" ? null : Number(e.target.value) })}
                     className="h-8 font-mono text-xs"
                     data-testid="trip-amount-to-pay" />
            </div>
            <div>
              <Label className="text-xs">Pay override reason</Label>
              <Input value={form.amount_to_pay_reason || ""}
                     onChange={(e) => setForm({ ...form, amount_to_pay_reason: e.target.value })}
                     placeholder="(optional)"
                     className="h-8 text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onCreate} className="brand-bg brand-bg-hover text-white" data-testid="trip-save-btn">Save trip</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!settleFor} onOpenChange={(o) => !o && setSettleFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Settle trip</DialogTitle>
          <DialogDescription>To Pay: <b>{fmt(settleFor?.amount_to_pay)}</b> · Pending: <b>{fmt(settleFor?.pending)}</b></DialogDescription>
          </DialogHeader>
          <Label className="text-xs">Amount paid (₹)</Label>
          <Input type="number" value={settleAmt} onChange={(e) => setSettleAmt(e.target.value)}
                 data-testid="trip-settle-amt" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSettleFor(null)}>Cancel</Button>
            <Button onClick={onSettle} className="brand-bg brand-bg-hover text-white"
                    data-testid="trip-settle-confirm">Confirm settle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterSel({ label, value, options, onChange }) {
  return (
    <div>
      <Label className="text-[10px]">{label}</Label>
      <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
        <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v,l]) => <SelectItem key={v || "__all"} value={v || "__all"}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

// ===========================================================================
// IMPREST
// ===========================================================================
function ImprestTab() {
  const [summary, setSummary] = useState({ rows: [] });
  const [selDriver, setSelDriver] = useState(null);
  const [statement, setStatement] = useState(null);
  const [recoverFor, setRecoverFor] = useState(null);
  const [recoverData, setRecoverData] = useState({ payroll_month: "", amount: "" });
  const [entryFor, setEntryFor] = useState(null);
  const [entryData, setEntryData] = useState({ entry_type: "topup", amount: "", note: "" });

  const refresh = () => api.get("/logistics/imprest/summary").then(r => setSummary(r.data));
  useEffect(() => { refresh(); }, []);

  const openStatement = async (driver) => {
    setSelDriver(driver);
    const r = await api.get(`/logistics/imprest/driver/${driver.id}`);
    setStatement(r.data);
  };

  const onPostEntry = async () => {
    try {
      await api.post("/logistics/imprest/entry", {
        driver_id: entryFor.id,
        entry_type: entryData.entry_type,
        amount: Number(entryData.amount),
        note: entryData.note,
      });
      toast.success(`${entryData.entry_type} posted`);
      setEntryFor(null);
      setEntryData({ entry_type: "topup", amount: "", note: "" });
      refresh();
      if (selDriver) openStatement(selDriver);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const onRecover = async () => {
    if (!recoverData.payroll_month || !recoverData.amount) {
      return toast.error("Month + amount required");
    }
    try {
      const r = await api.post("/logistics/imprest/recover-from-salary", {
        driver_id: recoverFor.id,
        payroll_month: recoverData.payroll_month,
        amount: Number(recoverData.amount),
      });
      toast.success(`Recovered ${fmt(recoverData.amount)} → new net ${fmt(r.data.new_net_salary)}`);
      setRecoverFor(null);
      setRecoverData({ payroll_month: "", amount: "" });
      refresh();
      if (selDriver) openStatement(selDriver);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Recovery failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="text-left px-3 py-2">Driver</th>
              <th className="text-right px-3 py-2">Imprest balance</th>
              <th className="text-right px-3 py-2">Open trips pending</th>
              <th className="text-right px-3 py-2">Outstanding</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {summary.rows.map(d => {
              const out = Math.max(0, d.pending - d.balance);
              return (
                <tr key={d.id} className="border-t border-slate-100" data-testid={`imprest-row-${d.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-sm">{d.name}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{d.employee_code}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono"
                      data-testid={`imprest-balance-${d.id}`}>{fmt(d.balance)}</td>
                  <td className="px-3 py-2 text-right font-mono"
                      data-testid={`imprest-pending-${d.id}`}>{fmt(d.pending)}</td>
                  <td className={`px-3 py-2 text-right font-mono font-bold ${out > 0 ? "text-red-700" : "text-slate-400"}`}>
                    {fmt(out)}
                  </td>
                  <td className="px-2 py-2 text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => openStatement(d)}>Statement</Button>
                    <Button size="sm" variant="outline"
                            onClick={() => { setEntryFor(d); setEntryData({ entry_type: "topup", amount: "", note: "" }); }}>
                      Entry
                    </Button>
                    <Button size="sm" variant="outline"
                            data-testid={`recover-salary-btn-${d.id}`}
                            disabled={out <= 0}
                            onClick={() => { setRecoverFor(d); setRecoverData({ payroll_month: "", amount: String(out) }); }}>
                      Recover
                    </Button>
                  </td>
                </tr>
              );
            })}
            {summary.rows.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-xs text-slate-400">No drivers configured.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!statement} onOpenChange={(o) => !o && setStatement(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Imprest statement — {selDriver?.name}</DialogTitle>
            <DialogDescription>
              Balance: <b>{fmt(statement?.balance)}</b> · Open pending: <b>{fmt(statement?.pending)}</b>
            </DialogDescription>
          </DialogHeader>
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-1.5">Date</th>
                <th className="text-left p-1.5">Type</th>
                <th className="text-right p-1.5">Amount</th>
                <th className="text-right p-1.5">Running</th>
                <th className="text-left p-1.5">Note</th>
              </tr>
            </thead>
            <tbody>
              {(statement?.ledger || []).map(e => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="p-1.5">{e.created_at?.slice(0,10)}</td>
                  <td className="p-1.5"><span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{e.entry_type}</span></td>
                  <td className={`p-1.5 text-right font-mono ${e.amount < 0 ? "text-red-700" : "text-emerald-700"}`}>
                    {e.amount > 0 ? "+" : ""}{fmt(e.amount)}
                  </td>
                  <td className="p-1.5 text-right font-mono">{fmt(e.running_balance)}</td>
                  <td className="p-1.5 text-slate-500">{e.note}</td>
                </tr>
              ))}
              {(!statement?.ledger?.length) && <tr><td colSpan={5} className="text-center py-4 text-slate-400">No entries.</td></tr>}
            </tbody>
          </table>
        </DialogContent>
      </Dialog>

      <Dialog open={!!entryFor} onOpenChange={(o) => !o && setEntryFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Imprest entry — {entryFor?.name}</DialogTitle>
            <DialogDescription>topup (+) / return (+) / adjustment (±, requires note)</DialogDescription>
          </DialogHeader>
          <Label className="text-xs">Type</Label>
          <Select value={entryData.entry_type} onValueChange={(v) => setEntryData({...entryData, entry_type: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="topup">Topup (+)</SelectItem>
              <SelectItem value="return">Return (+)</SelectItem>
              <SelectItem value="adjustment">Adjustment (±)</SelectItem>
            </SelectContent>
          </Select>
          <Label className="text-xs">Amount (₹)</Label>
          <Input type="number" value={entryData.amount} onChange={(e) => setEntryData({...entryData, amount: e.target.value})} />
          <Label className="text-xs">Note {entryData.entry_type === "adjustment" && <span className="text-red-600">*</span>}</Label>
          <Textarea rows={2} value={entryData.note} onChange={(e) => setEntryData({...entryData, note: e.target.value})} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEntryFor(null)}>Cancel</Button>
            <Button onClick={onPostEntry} className="brand-bg brand-bg-hover text-white">Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!recoverFor} onOpenChange={(o) => !o && setRecoverFor(null)}>
        <DialogContent data-testid="recover-dialog">
          <DialogHeader>
            <DialogTitle>Recover from salary — {recoverFor?.name}</DialogTitle>
            <DialogDescription>
              Adds the amount to <b>other_deductions</b> on the driver's draft payslip
              and posts a salary_recovery entry to the imprest ledger.
              Frozen once payroll is approved.
            </DialogDescription>
          </DialogHeader>
          <Label className="text-xs">Payroll month *</Label>
          <Input type="month" value={recoverData.payroll_month?.slice(0,7) || ""}
                 onChange={(e) => setRecoverData({...recoverData, payroll_month: e.target.value})}
                 data-testid="recover-month-input" />
          <Label className="text-xs">Amount (₹) *</Label>
          <Input type="number" value={recoverData.amount}
                 onChange={(e) => setRecoverData({...recoverData, amount: e.target.value})}
                 data-testid="recover-amount-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRecoverFor(null)}>Cancel</Button>
            <Button onClick={onRecover} className="brand-bg brand-bg-hover text-white"
                    data-testid="recover-confirm">Recover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ===========================================================================
// LM IMPREST POOL (master wallet)
// ===========================================================================
function LMImprestTab() {
  const [state, setState] = useState({ balance: 0, threshold: 3000, is_low: false, ledger: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    entry_type: "topup", amount: "", date: todayIso(), note: "",
  });

  const refresh = () => api.get("/logistics/lm-imprest").then(r => setState(r.data));
  useEffect(() => { refresh(); }, []);

  const onSave = async () => {
    if (!form.amount || Number(form.amount) === 0) {
      return toast.error("Amount required");
    }
    if (form.entry_type === "adjustment" && !form.note.trim()) {
      return toast.error("Note required for adjustments");
    }
    try {
      await api.post("/logistics/lm-imprest/entry", {
        entry_type: form.entry_type,
        amount: Number(form.amount),
        date: form.date,
        note: form.note || null,
      });
      toast.success("Saved");
      setOpen(false);
      setForm({ entry_type: "topup", amount: "", date: todayIso(), note: "" });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className={`rounded-lg p-4 border ${state.is_low ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">LM Imprest Pool</div>
          <div className={`text-3xl font-bold mt-1 ${state.is_low ? "text-red-700" : "text-emerald-700"}`}
               data-testid="lm-imprest-balance">
            {fmt(state.balance)}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            Threshold ₹{state.threshold?.toLocaleString("en-IN") || 0} ·
            {state.is_low ? <span className="text-red-700 font-semibold"> LOW — Accounts notified</span> : <span> healthy</span>}
          </div>
        </div>
        <div className="rounded-lg p-4 border bg-white">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Last Top-up</div>
          <div className="text-xl font-semibold mt-1 font-mono">
            {state.last_topup_amount ? fmt(state.last_topup_amount) : "—"}
          </div>
          <div className="text-[10px] text-slate-500">{state.last_topup_date || "No top-up yet"}</div>
        </div>
        <div className="rounded-lg p-4 border bg-white flex items-center justify-center">
          <Button onClick={() => { setForm({ entry_type: "topup", amount: "", date: todayIso(), note: "" }); setOpen(true); }}
                  className="brand-bg brand-bg-hover text-white"
                  data-testid="lm-imprest-topup-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Record Top-up / Adjustment
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-right px-3 py-2">Running</th>
              <th className="text-left px-3 py-2">Note / Ref</th>
              <th className="text-left px-3 py-2">By</th>
            </tr>
          </thead>
          <tbody>
            {state.ledger.map(r => (
              <tr key={r.id} className="border-t border-slate-100"
                  data-testid={`lm-ledger-row-${r.id}`}>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {r.date || r.created_at?.slice(0, 10)}
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    r.entry_type === "topup" ? "bg-emerald-100 text-emerald-800"
                      : r.entry_type === "trip_settle" ? "bg-blue-100 text-blue-800"
                      : "bg-slate-100 text-slate-700"
                  }`}>{r.entry_type.replace("_", " ")}</span>
                </td>
                <td className={`px-3 py-2 text-right font-mono font-semibold ${
                  r.amount >= 0 ? "text-emerald-700" : "text-red-700"
                }`}>
                  {r.amount >= 0 ? "+" : ""}{fmt(r.amount)}
                </td>
                <td className="px-3 py-2 text-right font-mono">{fmt(r.running_balance)}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.note || r.ref_id || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.created_by_name || "—"}</td>
              </tr>
            ))}
            {state.ledger.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-xs text-slate-400">
                No entries yet — record your first top-up from Accounts.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>LM Imprest Entry</DialogTitle>
            <DialogDescription className="text-xs">
              Top-up = cash received from Accounts (credit). Adjustment = signed correction (note required).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Entry type</Label>
            <Select value={form.entry_type} onValueChange={(v) => setForm({ ...form, entry_type: v })}>
              <SelectTrigger data-testid="lm-entry-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="topup">Top-up (received from Accounts)</SelectItem>
                <SelectItem value="adjustment">Adjustment (signed)</SelectItem>
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.date} max={todayIso()}
                       onChange={(e) => setForm({ ...form, date: e.target.value })}
                       data-testid="lm-entry-date" />
              </div>
              <div>
                <Label className="text-xs">Amount (₹) *</Label>
                <Input type="number" value={form.amount}
                       onChange={(e) => setForm({ ...form, amount: e.target.value })}
                       placeholder={form.entry_type === "adjustment" ? "Use - for debit" : "Positive number"}
                       data-testid="lm-entry-amount" />
              </div>
            </div>
            <Label className="text-xs">Note {form.entry_type === "adjustment" ? "*" : "(optional)"}</Label>
            <Input value={form.note}
                   onChange={(e) => setForm({ ...form, note: e.target.value })}
                   placeholder="e.g. Cash from accounts cheque #1234"
                   data-testid="lm-entry-note" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onSave} className="brand-bg brand-bg-hover text-white"
                    data-testid="lm-entry-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===========================================================================
// COMPLIANCE TAB (Phase 15.1) — Vehicle docs + Alerts
// ===========================================================================
function ComplianceTab() {
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);

  const load = async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/vehicles/compliance-summary"),
        api.get("/vehicle-alerts", { params: { status: "pending" } }),
      ]);
      setSummary(s.data);
      setAlerts(a.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setScanBusy(true);
    try {
      const { data } = await api.post("/vehicle-alerts/scan");
      toast.success(`Scan complete — ${data.alerts_created} new alerts, ${data.alerts_skipped} already alerted`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setScanBusy(false);
    }
  };

  if (!summary) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4">
      {/* Counts header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <DashTile label="Active Vehicles" value={summary.counts.vehicles} tone="blue" />
        <DashTile label="Expired Docs" value={summary.counts.expired} tone={summary.counts.expired > 0 ? "red" : "blue"} />
        <DashTile label="Expiring (30d)" value={summary.counts.expiring_soon} tone={summary.counts.expiring_soon > 0 ? "amber" : "blue"} />
        <DashTile label="Missing Docs" value={summary.counts.missing} tone={summary.counts.missing > 0 ? "amber" : "blue"} />
      </div>

      {/* Pending alerts */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-900">
            Pending Alerts ({alerts.length})
          </h3>
          <Button size="sm" onClick={runScan} disabled={scanBusy} data-testid="run-scan-btn">
            {scanBusy ? "Scanning…" : "Run Scan Now"}
          </Button>
        </div>
        {alerts.length === 0 ? (
          <div className="text-center text-xs text-slate-400 py-6">No pending alerts ✓</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-1">Target</th>
                <th className="text-left py-1">Doc</th>
                <th className="text-left py-1">Expiry</th>
                <th className="text-left py-1">Days</th>
                <th className="text-left py-1">Channel</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {alerts.slice(0, 50).map((a) => (
                <tr key={a.id} data-testid={`alert-${a.id}`}>
                  <td className="py-1.5">{a.vehicle_no || a.employee_name}</td>
                  <td className="py-1.5">{a.doc_type_label}</td>
                  <td className="py-1.5 font-mono">{a.expiry_date}</td>
                  <td className={`py-1.5 font-semibold ${a.days_left < 0 ? "text-red-600" : a.days_left <= 7 ? "text-red-500" : "text-amber-600"}`}>
                    {a.days_left < 0 ? `${Math.abs(a.days_left)} overdue` : `${a.days_left} days`}
                  </td>
                  <td className="py-1.5 text-slate-500 text-[10px]">{a.channel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Vehicle grid */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
          Vehicle Compliance Matrix
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Vehicle</th>
                {summary.doc_types.map((dt) => (
                  <th key={dt.key} className="text-center px-2 py-2">{dt.label.split(" ")[0]}</th>
                ))}
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {summary.rows.map((v) => (
                <tr key={v.vehicle_id} data-testid={`compliance-row-${v.vehicle_id}`}>
                  <td className="px-3 py-2 font-mono font-semibold">{v.vehicle_no}</td>
                  {v.statuses.map((st) => (
                    <td key={st.doc_type} className="text-center px-2 py-2">
                      {st.status === "valid" && (
                        <span title={`${st.expiry_date} (${st.days_left}d)`} className="text-emerald-600 font-bold">✓</span>
                      )}
                      {st.status === "expiring" && (
                        <span title={`${st.expiry_date} (${st.days_left}d)`} className="text-amber-600 font-bold">!</span>
                      )}
                      {st.status === "expired" && (
                        <span title={`${st.expiry_date}`} className="text-red-600 font-bold">✗</span>
                      )}
                      {st.status === "missing" && (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setSelectedVehicle(v)}
                      data-testid={`manage-docs-${v.vehicle_id}`}
                      className="text-bravura hover:underline text-[11px] font-semibold"
                    >
                      Manage docs
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedVehicle && (
        <VehicleDocsDialog
          vehicle={selectedVehicle}
          docTypes={summary.doc_types}
          onClose={() => { setSelectedVehicle(null); load(); }}
        />
      )}
    </div>
  );
}

function VehicleDocsDialog({ vehicle, docTypes, onClose }) {
  const [docs, setDocs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/vehicles/${vehicle.vehicle_id}/documents`);
      setDocs(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (payload, docId) => {
    setBusy(true);
    try {
      if (docId) {
        await api.patch(`/vehicles/documents/${docId}`, payload);
        toast.success("Document updated");
      } else {
        await api.post(`/vehicles/${vehicle.vehicle_id}/documents`, payload);
        toast.success("Document added");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/vehicles/documents/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 flex items-center justify-center p-4" data-testid="docs-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h4 className="text-base font-bold">Documents — {vehicle.vehicle_no}</h4>
            <p className="text-[11px] text-slate-500">{vehicle.model || ""}</p>
          </div>
          <button onClick={onClose}>✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="space-y-2">
            {docs.length === 0 && (
              <div className="text-center text-xs text-slate-400 py-6">No documents yet</div>
            )}
            {docs.map((d) => (
              <div key={d.id} className="bg-slate-50 rounded-lg p-3 flex items-center justify-between" data-testid={`doc-row-${d.id}`}>
                <div className="flex-1">
                  <div className="text-sm font-semibold">{d.doc_type_label}</div>
                  <div className="text-[11px] text-slate-500">
                    {d.doc_no && <span>#{d.doc_no} · </span>}
                    Expires {d.expiry_date}
                    <span className={`ml-2 ${d.expired ? "text-red-600" : d.expiring_soon ? "text-amber-600" : "text-emerald-600"}`}>
                      ({d.expired ? `${Math.abs(d.days_left)}d overdue` : `${d.days_left}d left`})
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing(d)} className="text-xs px-2 py-1 rounded text-bravura hover:bg-white">Edit</button>
                  <button onClick={() => del(d.id)} className="text-xs px-2 py-1 rounded text-red-600 hover:bg-white">Delete</button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <Button
              onClick={() => setEditing({})}
              data-testid="add-doc-btn"
              variant="outline"
              size="sm"
              className="w-full"
            >
              + Add Document
            </Button>
          </div>
        </div>
      </div>

      {editing !== null && (
        <DocEditDialog
          doc={editing}
          docTypes={docTypes}
          existingTypes={docs.filter(x => x.id !== editing.id).map(x => x.doc_type)}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSave={(p) => save(p, editing.id)}
        />
      )}
    </div>
  );
}

function DocEditDialog({ doc, docTypes, existingTypes, busy, onCancel, onSave }) {
  const [form, setForm] = useState({
    doc_type: doc.doc_type || (docTypes.find(t => !existingTypes.includes(t.key))?.key || "insurance"),
    doc_no: doc.doc_no || "",
    issue_date: doc.issue_date || "",
    expiry_date: doc.expiry_date || "",
    notes: doc.notes || "",
  });
  const submit = () => {
    if (!form.expiry_date) {
      toast.error("Expiry date is required");
      return;
    }
    onSave(form);
  };
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/70 flex items-center justify-center p-4" data-testid="doc-edit-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-3">
        <h5 className="text-base font-bold">{doc.id ? "Edit Document" : "Add Document"}</h5>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Type</label>
          <select
            value={form.doc_type}
            onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
            disabled={!!doc.id}
            data-testid="doc-type"
            className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
          >
            {docTypes.map(t => (
              <option key={t.key} value={t.key} disabled={!doc.id && existingTypes.includes(t.key)}>
                {t.label}{!doc.id && existingTypes.includes(t.key) ? " (added)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Doc No (optional)</label>
          <input
            type="text"
            value={form.doc_no}
            onChange={(e) => setForm({ ...form, doc_no: e.target.value })}
            data-testid="doc-no"
            className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Issue Date</label>
            <input
              type="date"
              value={form.issue_date}
              onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              data-testid="doc-issue"
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Expiry Date *</label>
            <input
              type="date"
              value={form.expiry_date}
              onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
              data-testid="doc-expiry"
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
            />
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Notes</label>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="doc-notes"
            className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
          />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="doc-save-btn" className="brand-bg text-white">
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}



// ===========================================================================
// SUREPASS TOLLS PANEL (Phase 15.5) — toll history + manual SBI Excel import
// ===========================================================================
function SurepassTollsPanel() {
  const [summary, setSummary] = useState(null);
  const [tolls, setTolls] = useState([]);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/tolls/summary"),
        api.get("/tolls", { params: { unassigned_only: unassignedOnly, limit: 100 } }),
      ]);
      setSummary(s.data);
      setTolls(t.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, [unassignedOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshSurepass = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tolls/refresh", null, { params: { since_days: 7 } });
      if (!data.live) {
        toast.info("Surepass API key not configured — running in mock mode. Use SBI Excel import.");
      } else {
        toast.success(`Pulled ${data.transactions_inserted} new tolls (${data.transactions_duplicate} dupes, ${data.vehicles_processed} vehicles)`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const importSbi = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tolls/import-sbi-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted} tolls (${data.duplicates} duplicates, ${data.skipped} skipped)`);
      e.target.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!summary) return null;

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-xl p-4 space-y-3" data-testid="surepass-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            Surepass Toll History
            {summary.surepass_live ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">LIVE</span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">MOCK · use SBI Excel</span>
            )}
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Toll deductions are spend-visibility only — they don't debit imprest.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={refreshSurepass} disabled={busy} data-testid="refresh-surepass-btn">
            {busy ? "…" : "Refresh from Surepass"}
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".csv" onChange={importSbi} className="hidden" data-testid="import-sbi-input" disabled={busy} />
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-bravura text-white text-xs font-bold hover:bg-bravura-700">
              Import SBI CSV
            </span>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="bg-white rounded p-2 ring-1 ring-indigo-100">
          <div className="text-[10px] uppercase text-slate-500">Total Tolls</div>
          <div className="text-base font-bold text-slate-900">{summary.total}</div>
        </div>
        <div className="bg-white rounded p-2 ring-1 ring-red-100">
          <div className="text-[10px] uppercase text-slate-500">Unassigned</div>
          <div className={`text-base font-bold ${summary.unassigned > 0 ? "text-red-600" : "text-slate-900"}`}>
            {summary.unassigned}
          </div>
        </div>
        {summary.by_source.map((s) => (
          <div key={s.source} className="bg-white rounded p-2 ring-1 ring-slate-100">
            <div className="text-[10px] uppercase text-slate-500">{s.source === "surepass" ? "Surepass" : "SBI Manual"}</div>
            <div className="text-base font-bold text-slate-900">{s.count}</div>
            <div className="text-[10px] text-slate-500">₹{Math.round(s.amount || 0).toLocaleString("en-IN")}</div>
          </div>
        ))}
      </div>

      {tolls.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] flex items-center gap-1 text-slate-600">
              <input type="checkbox" checked={unassignedOnly} onChange={(e) => setUnassignedOnly(e.target.checked)} data-testid="unassigned-filter" />
              Show unassigned only
            </label>
            <span className="text-[10px] text-slate-400">Showing {tolls.length} latest</span>
          </div>
          <div className="bg-white border border-slate-100 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5">Vehicle</th>
                  <th className="text-left px-2 py-1.5">Plaza</th>
                  <th className="text-right px-2 py-1.5">Amount</th>
                  <th className="text-left px-2 py-1.5">Date</th>
                  <th className="text-left px-2 py-1.5">Trip</th>
                  <th className="text-left px-2 py-1.5">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tolls.map((t) => (
                  <tr key={t.id} data-testid={`toll-${t.id}`} className={t.unassigned ? "bg-amber-50/40" : ""}>
                    <td className="px-2 py-1.5 font-mono">{t.vehicle_no}</td>
                    <td className="px-2 py-1.5">{t.plaza || "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono">₹{t.amount}</td>
                    <td className="px-2 py-1.5 text-[10px] text-slate-500">{(t.datetime || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-2 py-1.5 text-[10px]">
                      {t.trip_id ? <span className="text-emerald-700">matched</span> : <span className="text-amber-600">unassigned</span>}
                    </td>
                    <td className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500">{t.source.replace("manual_", "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ===========================================================================
// PHASE 15.10 — FASTag LEDGER (unified timeline + manual balance + refresh)
// ===========================================================================
function FastagLedgerTab() {
  const [trucks, setTrucks] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [filters, setFilters] = useState({
    truck_no: "", from_date: "", to_date: "", limit: 500,
  });
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({
    truck_no: "", balance: "", amount: "", plaza: "", location: "", note: "",
  });

  const loadTrucks = async () => {
    try {
      const r = await api.get("/logistics/trucks");
      setTrucks((r.data || []).filter((t) => t.active !== false && (t.fastag_tag_id || t.fastag_no)));
    } catch (e) { /* ignore */ }
  };

  const loadLedger = async () => {
    setBusy(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/tolls/ledger", { params });
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load ledger");
    } finally {
      setBusy(false);
    }
  };

  const loadLastRun = async () => {
    try {
      const r = await api.get("/tolls/refresh-runs", { params: { limit: 1 } });
      setLastRun((r.data || [])[0] || null);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadTrucks(); loadLastRun(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLedger(); }, [JSON.stringify(filters)]);

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/tolls/refresh-all");
      const d = r.data || {};
      if (d.live === false) {
        toast.info(`Mock mode · ${d.vehicles_processed || 0} trucks scanned`);
      } else {
        toast.success(
          `Refreshed · ${d.vehicles_processed || 0} trucks · ${d.transactions_inserted || 0} new tolls`,
        );
      }
      await Promise.all([loadLedger(), loadLastRun()]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const toggleAutoRefresh = async (truck, enabled) => {
    try {
      await api.post("/tolls/auto-refresh-toggle", {
        truck_no: truck.truck_no, enabled,
      });
      setTrucks((prev) => prev.map((t) =>
        t.id === truck.id ? { ...t, auto_fastag_refresh: enabled } : t,
      ));
      toast.success(`${truck.truck_no} · auto-refresh ${enabled ? "ON" : "OFF"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Toggle failed");
    }
  };

  const openManual = (truck = null) => {
    setManual({
      truck_no: truck?.truck_no || "",
      balance: "", amount: "", plaza: "", location: "", note: "",
    });
    setManualOpen(true);
  };

  const submitManual = async () => {
    if (!manual.truck_no || manual.balance === "") {
      toast.error("Truck and balance are required");
      return;
    }
    try {
      const payload = {
        truck_no: manual.truck_no,
        balance: Number(manual.balance),
        plaza: manual.plaza || null,
        location: manual.location || null,
        note: manual.note || null,
        amount: manual.amount === "" ? null : Number(manual.amount),
      };
      await api.post("/tolls/balance/manual", payload);
      toast.success("Manual balance recorded");
      setManualOpen(false);
      loadLedger();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const summary = useMemo(() => {
    let crossingCount = 0;
    let totalDeducted = 0;
    let latestBalance = null;
    let latestDt = "";
    const byTruck = new Map();
    rows.forEach((r) => {
      if (r.type === "crossing") {
        crossingCount += 1;
        totalDeducted += Math.abs(Number(r.amount || 0));
      }
      if (r.balance != null) {
        const dt = r.datetime || "";
        const cur = byTruck.get(r.truck_no);
        if (!cur || dt > cur.dt) byTruck.set(r.truck_no, { dt, bal: Number(r.balance) });
        if (dt > latestDt) { latestDt = dt; latestBalance = Number(r.balance); }
      }
    });
    const totalBal = Array.from(byTruck.values()).reduce((s, x) => s + x.bal, 0);
    return { crossingCount, totalDeducted, latestBalance, totalBal, trucksWithBal: byTruck.size };
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="fastag-ledger-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Receipt className="h-4 w-4 text-indigo-600" />
            FASTag Ledger
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800">
              Auto · 6 AM &amp; 6 PM IST
            </span>
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Unified timeline · Date | Truck | Plaza | Location | Amount | Balance.
            {lastRun?.started_at && (
              <span className="ml-1">
                Last run: <b>{(lastRun.started_at || "").slice(0, 19).replace("T", " ")}</b>
                {" · "}{lastRun.vehicles_processed ?? 0} trucks
                {" · "}{lastRun.transactions_inserted ?? 0} new
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => openManual()}
                  data-testid="manual-balance-btn">
            <Wallet className="h-3.5 w-3.5 mr-1" /> Manual Balance
          </Button>
          <Button size="sm" onClick={refreshNow} disabled={refreshing}
                  data-testid="refresh-now-btn"
                  className="brand-bg brand-bg-hover text-white">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh Now"}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <DashTile label="Total crossings" value={summary.crossingCount} tone="blue" />
        <DashTile label="Total deducted" value={fmt(summary.totalDeducted)} tone="red" />
        <DashTile label="Sum latest balance" value={fmt(summary.totalBal)}
                  note={`${summary.trucksWithBal} truck(s)`} tone="amber" />
        <DashTile label="Latest balance event"
                  value={summary.latestBalance == null ? "—" : fmt(summary.latestBalance)}
                  tone="blue" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Truck</Label>
          <Select value={filters.truck_no || "all"}
                  onValueChange={(v) => setFilters((f) => ({ ...f, truck_no: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="ledger-truck-filter">
              <SelectValue placeholder="All trucks" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All trucks</SelectItem>
              {trucks.map((t) => (
                <SelectItem key={t.id} value={t.truck_no}>{t.truck_no}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">From</Label>
          <Input type="date" value={filters.from_date}
                 max={todayIso()}
                 onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
                 data-testid="ledger-from-date" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">To</Label>
          <Input type="date" value={filters.to_date}
                 max={todayIso()}
                 onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
                 data-testid="ledger-to-date" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm"
                  onClick={() => setFilters({ truck_no: "", from_date: "", to_date: "", limit: 500 })}
                  data-testid="ledger-reset-filters">
            Reset
          </Button>
          <Button variant="outline" size="sm" onClick={loadLedger} disabled={busy}
                  data-testid="ledger-reload">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Reload
          </Button>
        </div>
      </div>

      {/* Auto-refresh toggles per truck */}
      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
          Auto-Refresh per Truck (6 AM &amp; 6 PM IST)
        </div>
        {trucks.length === 0 ? (
          <div className="text-xs text-slate-400">No trucks with FASTag tag configured.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {trucks.map((t) => {
              const enabled = t.auto_fastag_refresh !== false;
              return (
                <label key={t.id}
                       className={`inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-md ring-1 cursor-pointer
                                   ${enabled ? "bg-emerald-50 ring-emerald-200 text-emerald-800"
                                              : "bg-slate-50 ring-slate-200 text-slate-500"}`}
                       data-testid={`auto-refresh-pill-${t.truck_no}`}>
                  <Switch checked={enabled}
                          onCheckedChange={(v) => toggleAutoRefresh(t, v)}
                          data-testid={`auto-refresh-toggle-${t.truck_no}`} />
                  <span className="font-mono">{t.truck_no}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Ledger table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[1100px] text-sm" data-testid="ledger-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Truck</th>
              <th className="text-left px-3 py-2">Plaza</th>
              <th className="text-left px-3 py-2">Location</th>
              <th className="text-right px-3 py-2">Amount (₹)</th>
              <th className="text-right px-3 py-2">Balance (₹)</th>
              <th className="text-center px-3 py-2">Type</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const isDebit = r.type === "crossing";
              return (
                <tr key={`${r.id || idx}-${r.type}`}
                    className="border-t border-slate-100"
                    data-testid={`ledger-row-${r.type}-${idx}`}>
                  <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                    {(r.datetime || "").slice(0, 19).replace("T", " ")}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.truck_no || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.plaza || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.location || "—"}</td>
                  <td className={`px-3 py-2 text-right font-mono text-xs
                                  ${isDebit ? "text-red-700" : "text-slate-500"}`}>
                    {r.amount == null ? "—"
                      : (isDebit ? "-" : "") + fmt(Math.abs(Number(r.amount || 0)))}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {r.balance == null ? "—" : fmt(r.balance)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {isDebit ? (
                      <span className="text-[10px] bg-red-50 text-red-700 px-1.5 py-0.5 rounded ring-1 ring-red-100">
                        Crossing
                      </span>
                    ) : (
                      <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded ring-1 ring-emerald-100">
                        {(r.source || "manual").toString().toUpperCase()}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && !busy && (
              <tr><td colSpan={7} className="text-center py-8 text-xs text-slate-400">
                No ledger entries. Try "Refresh Now" or record a manual balance.
              </td></tr>
            )}
            {busy && (
              <tr><td colSpan={7} className="text-center py-8 text-xs text-slate-400">
                Loading…
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Manual balance modal */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record manual FASTag balance</DialogTitle>
            <DialogDescription className="text-xs">
              Use this when refreshing from a toll booth slip or a recharge confirmation.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Truck</Label>
              <Select value={manual.truck_no}
                      onValueChange={(v) => setManual((m) => ({ ...m, truck_no: v }))}>
                <SelectTrigger data-testid="manual-truck-select">
                  <SelectValue placeholder="Select truck" />
                </SelectTrigger>
                <SelectContent>
                  {trucks.map((t) => (
                    <SelectItem key={t.id} value={t.truck_no}>{t.truck_no}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Balance (₹) *</Label>
              <Input type="number" value={manual.balance}
                     onChange={(e) => setManual((m) => ({ ...m, balance: e.target.value }))}
                     data-testid="manual-balance-input" />
            </div>
            <div>
              <Label className="text-xs">Deducted amount (₹, optional)</Label>
              <Input type="number" value={manual.amount}
                     onChange={(e) => setManual((m) => ({ ...m, amount: e.target.value }))}
                     data-testid="manual-amount-input" />
            </div>
            <div>
              <Label className="text-xs">Plaza (optional)</Label>
              <Input value={manual.plaza}
                     onChange={(e) => setManual((m) => ({ ...m, plaza: e.target.value }))}
                     data-testid="manual-plaza-input" />
            </div>
            <div>
              <Label className="text-xs">Location (optional)</Label>
              <Input value={manual.location}
                     onChange={(e) => setManual((m) => ({ ...m, location: e.target.value }))}
                     data-testid="manual-location-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Note</Label>
              <Textarea rows={2} value={manual.note}
                        onChange={(e) => setManual((m) => ({ ...m, note: e.target.value }))}
                        placeholder="e.g. Recharged ₹2000 via Paytm ref 123"
                        data-testid="manual-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button onClick={submitManual} className="brand-bg brand-bg-hover text-white"
                    data-testid="manual-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
